import { isArray } from "@/utils/is";
import type { FieldNamesProps } from "@/views/components/ProTable/interface";
import { getValueByProp } from "@/utils/object";
import type { Moment } from "moment";
import moment from "moment";
import { CDN_URL, DATE_PICKER_FORMAT, DATETIME_PICKER_FORMAT, DEFAULT_VALUE, LOG_DATE_FORMAT } from "@/config";
import type { ReactPermissionRoute } from "@/stores/react/authStore";

/**
 * @description 获取localStorage
 * @param {String} key Storage名称
 * @returns {String}
 */
export function localGet(key: string) {
    const value = window.localStorage.getItem(`zhihao-page${key}`);
    try {
        return JSON.parse(window.localStorage.getItem(`zhihao-page${key}`) as string);
    } catch {
        return value;
    }
}

/**
 * @description 存储localStorage
 * @param {String} key Storage名称
 * @param {*} value Storage值
 * @returns {void}
 */
export function localSet(key: string, value: any) {
    window.localStorage.setItem(`zhihao-page${key}`, JSON.stringify(value));
}

/**
 * @description 生成唯一 uuid
 * @returns {String}
 */
export function generateUUID() {
    let uuid = "";
    for (let i = 0; i < 32; i++) {
        const random = (Math.random() * 16) | 0;
        if (i === 8 || i === 12 || i === 16 || i === 20) uuid += "-";
        uuid += (i === 12 ? 4 : i === 16 ? (random & 3) | 8 : random).toString(16);
    }
    return uuid;
}

/**
 * @description 使用递归扁平化菜单，方便添加动态路由
 * @param {Array} menuList 菜单列表
 * @returns {Array}
 */
export function getFlatMenuList(menuList: ReactPermissionRoute[]): ReactPermissionRoute[] {
    const newMenuList: ReactPermissionRoute[] = JSON.parse(JSON.stringify(menuList));
    return newMenuList.flatMap(item => [item, ...(item.children ? getFlatMenuList(item.children) : [])]);
}

/**
 * @description 使用递归过滤出需要渲染在左侧菜单的列表 (需剔除 isHide == true 的菜单)
 * @param {Array} menuList 菜单列表
 * @returns {Array}
 * */
export function getShowMenuList(menuList: ReactPermissionRoute[]) {
    const newMenuList: ReactPermissionRoute[] = JSON.parse(JSON.stringify(menuList));
    return newMenuList.filter(item => {
        item.children?.length && (item.children = getShowMenuList(item.children));
        return !item.meta?.isHide;
    });
}

/**
 * @description 使用递归找出所有面包屑存储到 pinia/vuex 中
 * @param {Array} menuList 菜单列表
 * @param {Array} parent 父级菜单
 * @param {Object} result 处理后的结果
 * @returns {Object}
 */
export const getAllBreadcrumbList = (
    menuList: ReactPermissionRoute[],
    parent: ReactPermissionRoute[] = [],
    result: { [key: string]: any } = {}
) => {
    for (const item of menuList) {
        const path = item.path || item.name || "";
        result[path] = [...parent, item];
        if (item.children) getAllBreadcrumbList(item.children, result[path], result);
    }
    return result;
};

/**
 * @description 使用递归过滤需要缓存的菜单 name (该函数暂未使用)
 * @param {Array} menuList 所有菜单列表
 * @param {Array} keepAliveNameArr 缓存的菜单 name ['**','**']
 * @returns {Array}
 * */
export function getKeepAliveRouterName(menuList: ReactPermissionRoute[], keepAliveNameArr: string[] = []) {
    menuList.forEach(item => {
        item.meta?.isKeepAlive && typeof item.name === "string" && keepAliveNameArr.push(item.name);
        item.children?.length && getKeepAliveRouterName(item.children, keepAliveNameArr);
    });
    return keepAliveNameArr;
}

/**
 * @description 处理 ProTable 值为数组 || 无数据
 * @param {*} callValue 需要处理的值
 * @returns {String}
 * */
export function formatValue(callValue: any) {
    // 如果当前值为数组，使用 / 拼接（根据需求自定义）
    if (isArray(callValue)) return callValue.length ? callValue.join(" / ") : DEFAULT_VALUE;
    return callValue ?? DEFAULT_VALUE;
}

/**
 * @description 处理 prop 为多级嵌套的情况，返回的数据 (列如: prop: user.name)
 * @param {Object} row 当前行数据
 * @param {String} prop 当前 prop
 * @returns {*}
 * */
export function handleRowAccordingToProp(row: { [key: string]: any }, prop: string) {
    if (!prop.includes(".")) return row[prop] ?? DEFAULT_VALUE;
    prop.split(".").forEach(item => (row = row[item] ?? DEFAULT_VALUE));
    return row;
}

/**
 * @description 处理 prop，当 prop 为多级嵌套时 ==> 返回最后一级 prop
 * @param {String} prop 当前 prop
 * @returns {String}
 * */
export function handleProp(prop: string) {
    const propArr = prop.split(".");
    if (propArr.length == 1) return prop;
    return propArr[propArr.length - 1];
}

/**
 * @description 根据枚举列表查询当需要的数据（如果指定了 label 和 value 的 key值，会自动识别格式化）
 * @param {String} callValue 当前单元格值
 * @param {Array} enumData 字典列表
 * @param {Array} fieldNames label && value && children 的 key 值
 * @param {String} type 过滤类型（目前只有 tag）
 * @returns {String}
 * */
export function filterEnum(callValue: any, enumData?: any, fieldNames?: FieldNamesProps, type?: "tag") {
    const value = fieldNames?.value ?? "value";
    const label = fieldNames?.label ?? "label";
    const children = fieldNames?.children ?? "children";
    let filterData: { [key: string]: any } = {};
    // 判断 enumData 是否为数组
    if (Array.isArray(enumData)) filterData = findItemNested(enumData, callValue, value, children);
    // 判断是否输出的结果为 tag 类型
    if (type == "tag") {
        return filterData?.tagType ? filterData.tagType : "";
    } else {
        return filterData ? filterData[label] : DEFAULT_VALUE;
    }
}

/**
 * @description 根据枚举列表查询当需要的数据对象（如果指定了 label 和 value 的 key值，会自动识别格式化）
 * @param {String} callValue 当前单元格值
 * @param {Array} enumData 字典列表
 * @param {Array} fieldNames label && value && children 的 key 值
 * @returns {String}
 * */
export function filterEnumObj(callValue: any, enumData?: any, fieldNames?: FieldNamesProps) {
    const value = fieldNames?.value ?? "value";
    const children = fieldNames?.children ?? "children";
    let filterData: { [key: string]: any } = {};
    // 判断 enumData 是否为数组
    if (Array.isArray(enumData)) filterData = findItemNested(enumData, callValue, value, children);
    return filterData;
}

/**
 * @description 递归查找 callValue 对应的 enum 值
 * */
export function findItemNested(enumData: any, callValue: any, value: string, children: string) {
    return enumData.reduce((accumulator: any, current: any) => {
        if (accumulator) return accumulator;
        if (current[value] === callValue) return current;
        if (current[children]) return findItemNested(current[children], callValue, value, children);
    }, null);
}

export const $get = getValueByProp;

/**时间格式化 */
export const dateFormat = (str?: string | Date | Moment, format: string = DATE_PICKER_FORMAT) =>
    str ? moment(str).format(format).replace("Invalid date", DEFAULT_VALUE) : DEFAULT_VALUE;
export const dateTimeFormat = (str?: string | Date | Moment, format: string = DATETIME_PICKER_FORMAT) =>
    str ? moment(str).format(format).replace("Invalid date", DEFAULT_VALUE) : DEFAULT_VALUE;
export const dateFullTimeFormat = (str?: string | Date | Moment, format: string = LOG_DATE_FORMAT) =>
    str ? moment(str).format(format).replace("Invalid date", DEFAULT_VALUE) : DEFAULT_VALUE;

/**空串转undefined */
export const empty2Undefined = <T extends Record<string, any>>(obj: T): T => {
    Object.keys(obj).forEach((key: keyof T) => {
        if (obj[key] === "") obj[key] = undefined as any;
    });
    return obj;
};

/**拼接文件路径 */
export const getPublicFileUrl = (path: string) => {
    return CDN_URL + path;
};

/**合同相关获取真实state */
// 获取真实的state
export const getRealyState = (state: number | string): number => {
    // 先获取高位值
    // 若在签署中，再获取低位值
    // if (code === lowStateCode) code = Number(state) << 16;
    return Number(state) >> 16;
};

// 获取最近一年内的最近N月的月份列表
export const getLastYearMonths = (n: number) => {
    n = n > 12 ? 12 : n;
    const now = new Date();
    const lastYear = new Date(now.getFullYear() - 1, now.getMonth(), 1);
    const months = [];

    for (let i = 0; i < 12; i++) {
        const month = new Date(lastYear);
        month.setMonth(lastYear.getMonth() + i);
        const num = month.getMonth() + 1 + 1 === 13 ? 1 : month.getMonth() + 1 + 1;
        months.push(`${num}月`); // 加1因为getMonth()返回的月份是从0开始的
    }
    return months.slice(-n);
};

/** 获取某个月全部周的区间文本 */
export const getMonthWeekRangeString = (dateTime: string) => {
    const weeks = [];
    const count = moment(dateTime).daysInMonth() === 28 ? 4 : 5;
    for (let i = 0; i < count; i++) {
        const beginDate = moment(dateTime)
            .add(i * 7, "days")
            .format("MM-DD");
        let endTime = moment(dateTime)
            .add(i * 7 + 6, "days")
            .format("MM-DD");
        if (i === 4) {
            // 最后到月底
            endTime = moment(dateTime).endOf("M").format("MM-DD");
        }
        weeks.push(`${beginDate} 至 ${endTime}`);
    }
    return weeks;
};

// 从待办跳转列表页，获取缓存状态
export const getDaiBanState = (propsName: string) => {
    const obj = JSON.parse(localStorage.getItem("MytaskGotoListPageQuery") || "{}");
    const keys = Object.keys(obj);
    if (!keys.length || !keys.includes(propsName)) return [];
    const key = keys[0];
    return [obj[key]];
};

// 下拉框清空待办状态
export const clearDaiBanState = (vals: number[], propsName: string) => {
    if (getDaiBanState(propsName)?.length && !vals?.length) {
        localStorage.removeItem("MytaskGotoListPageQuery");
    }
};
